import {
    ReactElement,
    useRef,
    ChangeEventHandler,
    FormEventHandler,
    forwardRef,
    useCallback,
    useEffect,
    useImperativeHandle,
    useMemo,
    useState
} from 'react';
import { Team } from '@/api';
import { useUpdateTeamAction, useUploadTeamImageAction } from '../actions';
import {
    UIToast,
    UIAvatar,
    UIButton,
    UIFormInput,
    UIFormInputRef,
    UISection,
    UISectionContent,
    UISectionFooter,
    UISectionHeader,
    UIText
} from '@/ui';
import { useCompaniesTranslation } from '../hooks';

type GeneralTeamSettingsSectionProps = { team: Team };

export function GeneralTeamSettingsSection(
    props: GeneralTeamSettingsSectionProps
): ReactElement {
    const { team } = props;
    const { t } = useCompaniesTranslation();

    const formRef = useRef<GeneralTeamSettingsSectionFormRef>(null);
    const updateTeam = useUpdateTeamAction({ teamId: team.id });
    const uploadTeamImage = useUploadTeamImageAction({ teamId: team.id });

    function onSubmit(data: { teamName: string }) {
        const { teamName } = data;
        updateTeam.updateTeam({ name: teamName }).catch(() => {}); // Error handled in the action
    }

    function onUploadImage(file: File) {
        uploadTeamImage
            .uploadImage(file)
            .then(() => {
                UIToast.success(
                    t(
                        'components.general_team_settings_section.toast_image_updated'
                    )
                );
            })
            .catch(() => {}); // Error handled in the action
    }

    return (
        <GeneralTeamSettingsSectionForm
            canEdit={updateTeam.isAllowed}
            ref={formRef}
            initialTeamName={team.name}
            initialTeamImage={team.image}
            loading={updateTeam.loading}
            imageLoading={uploadTeamImage.loading}
            onSubmit={onSubmit}
            onUploadImage={onUploadImage}
        />
    );
}

type GeneralTeamSettingsSectionFormProps = {
    canEdit: boolean;
    initialTeamName: string;
    initialTeamImage: string;
    imageLoading: boolean;
    loading: boolean;
    onSubmit: (data: { teamName: string }) => void;
    onUploadImage: (file: File) => void;
};

export type GeneralTeamSettingsSectionFormRef = {
    resetForm: () => void;
};

const GeneralTeamSettingsSectionForm = forwardRef<
    GeneralTeamSettingsSectionFormRef,
    GeneralTeamSettingsSectionFormProps
>((props, ref): ReactElement => {
    const {
        canEdit,
        initialTeamName,
        initialTeamImage,
        loading,
        onSubmit,
        onUploadImage
    } = props;
    const { t } = useCompaniesTranslation();

    const fileInputRef = useRef<HTMLInputElement>(null);
    const handleFileInputClick = () => {
        fileInputRef.current?.click();
    };

    const handleFileChange: ChangeEventHandler<HTMLInputElement> = (event) => {
        if (event.target.files) {
            const file = event.target.files[0];
            if (file) {
                onUploadImage(file);
            }
        }
    };

    const [showRealTimeErrors, setShowRealTimeErrors] = useState(false);

    const teamNameRef = useRef<UIFormInputRef>(null);
    const [teamName, setTeamName] = useState(initialTeamName);
    const [teamNameError, setTeamNameError] = useState('');
    const checkTeamName = useCallback(
        (teamName: string): string => {
            const trimmed = teamName.trim();
            if (trimmed.length === 0) {
                return t(
                    'components.general_team_settings_section.name_error_empty'
                );
            } else if (trimmed.length > 100) {
                return t(
                    'components.general_team_settings_section.name_error_too_long'
                );
            }
            return '';
        },
        [t]
    );
    useEffect(() => {
        if (showRealTimeErrors) {
            setTeamNameError(checkTeamName(teamName));
        }
    }, [teamName, showRealTimeErrors, checkTeamName]);

    const handleSubmit: FormEventHandler = (e) => {
        e.preventDefault();

        setShowRealTimeErrors(true);

        if (!checkTeamName(teamName)) {
            setShowRealTimeErrors(false);
            onSubmit?.({ teamName });
        }
    };

    const haveValuesChanged = useMemo(() => {
        return teamName !== initialTeamName;
    }, [teamName, initialTeamName]);

    function resetForm() {
        setTeamName(initialTeamName);
        setTeamNameError('');
        setShowRealTimeErrors(false);
    }

    useImperativeHandle(ref, () => ({
        resetForm() {
            resetForm();
        }
    }));

    return (
        <UISection>
            <form onSubmit={handleSubmit} noValidate>
                <UISectionHeader title={t('shared.labels.general')} />
                <UISectionContent>
                    <div
                        style={{
                            display: 'flex',
                            flexDirection: 'column',
                            gap: 24
                        }}
                    >
                        <div
                            style={{
                                display: 'flex',
                                flexDirection: 'row',
                                alignItems: 'center',
                                gap: 16
                            }}
                        >
                            <div
                                style={{
                                    display: 'flex',
                                    flex: 1,
                                    gap: 16,
                                    alignItems: 'center'
                                }}
                            >
                                <UIAvatar
                                    size={64}
                                    img={initialTeamImage}
                                    alt=""
                                />
                                <div
                                    style={{
                                        display: 'flex',
                                        flexDirection: 'column',
                                        gap: 4
                                    }}
                                >
                                    <UIText>
                                        {t(
                                            'components.general_team_settings_section.image_label'
                                        )}
                                    </UIText>
                                    <UIText type="default2">
                                        {t(
                                            'components.general_team_settings_section.image_description'
                                        )}
                                    </UIText>
                                </div>
                            </div>

                            <div
                                style={{
                                    display: 'flex',
                                    flex: 1
                                }}
                            >
                                {canEdit && (
                                    <>
                                        <UIButton
                                            text={t('shared.labels.edit')}
                                            type="button"
                                            onClick={handleFileInputClick}
                                            loading={props.imageLoading}
                                            disabled={props.imageLoading}
                                        />
                                        <input
                                            type="file"
                                            ref={fileInputRef}
                                            style={{ display: 'none' }}
                                            accept=".jpg,.jpeg,.png"
                                            onChange={handleFileChange}
                                        />
                                    </>
                                )}
                            </div>
                        </div>
                        <div
                            style={{
                                display: 'flex',
                                flexDirection: 'row',
                                alignItems: 'center',
                                gap: 16
                            }}
                        >
                            <div
                                style={{
                                    display: 'flex',
                                    flex: 1,
                                    flexDirection: 'column',
                                    gap: 4
                                }}
                            >
                                <UIText>
                                    {t(
                                        'components.general_team_settings_section.name_label'
                                    )}
                                </UIText>
                                <UIText type="default2">
                                    {t(
                                        'components.general_team_settings_section.name_description'
                                    )}
                                </UIText>
                            </div>

                            <div
                                style={{
                                    display: 'flex',
                                    flex: 1
                                }}
                            >
                                <UIFormInput
                                    ref={teamNameRef}
                                    name="Team Name"
                                    value={teamName}
                                    placeholder={t(
                                        'components.general_team_settings_section.name_placeholder'
                                    )}
                                    type="text"
                                    autoComplete="on"
                                    errorText={teamNameError}
                                    required
                                    maxLength={100}
                                    onChange={setTeamName}
                                    disabled={!canEdit}
                                />
                            </div>
                        </div>
                    </div>
                </UISectionContent>
                {haveValuesChanged && (
                    <UISectionFooter
                        actions={[
                            {
                                variant: 'primary',
                                type: 'submit',
                                loading: loading,
                                disabled: loading,
                                text: loading
                                    ? t('shared.labels.saving')
                                    : t('shared.labels.save')
                            },
                            {
                                text: t('shared.labels.cancel'),
                                disabled: loading,
                                onClick: resetForm
                            }
                        ]}
                    />
                )}
            </form>
        </UISection>
    );
});
